import {Injectable} from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {INTERFACES_CHANNEL} from "./interfaces.registry";
import {Method, Site} from "../users/users.types";
import {ElapsedTimePipe} from "../../../../../@fuse/pipes/elapsed-time";
import {DcsConfig, DcsStatus, Variable} from "./interfaces.types";
import {getBackendUri, removeEmpty} from "../../../../shared/utils";

@Injectable({
    providedIn: 'root'
})
export class InterfacesService {
    private _httpOptions = {headers: new HttpHeaders({'Content-Type': 'application/json'})};
    private baseURL = getBackendUri();
    private _dcsConfigs: BehaviorSubject<DcsConfig[] | null> = new BehaviorSubject(null);
    private _dcsStatus: BehaviorSubject<DcsStatus[] | null> = new BehaviorSubject([]);
    private _dcsLogs: BehaviorSubject<any> = new BehaviorSubject({});
    private _maxLogs = 500;

    private _variables: BehaviorSubject<Variable[]> = new BehaviorSubject([]);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient,
                private _elapsedTimePipe: ElapsedTimePipe,) {
    }

    set updateDcsStatus(value: DcsStatus[]) {
        this._dcsStatus.next(value);
    }

    get dcsStatus(): BehaviorSubject<DcsStatus[]> {
        return this._dcsStatus;
    }

    set clearLogs(value: string) {
        const newState = this._dcsLogs.getValue();
        newState[value] = [];
        this._dcsLogs.next(newState);
    }

    set updateDcsLogs(value: any) {
        if (!this._dcsLogs.getValue()[value.container]) {
            this._dcsLogs.getValue()[value.container] = [];
        }
        const newState = this._dcsLogs.getValue();
        newState[value.container].push(value);
        if (newState[value.container].length > this._maxLogs) {
            newState[value.container].shift();
        }
        this._dcsLogs.next(newState);
    }

    get dcsLogs$(): Observable<any> {
        return this._dcsLogs.asObservable();
    }

    get variables$(): Observable<Variable[]> {
        return this._variables.asObservable();
    }

    /**
     * Getter for dcsConfigs
     */
    get dcsConfigs$(): Observable<DcsConfig[]> {
        return this._dcsConfigs.asObservable();
    }

    /**
     * Get configs
     */
    getDcsConfig(): Observable<DcsConfig[]> {
        //const body = `{ "query": "query { dcsConfig { dcsId siteId queue {q_limit p_limit s_limit time time_other} projectParsing {useStaticMapping roomNumberSuffix leadingZeros} hotdash_api {urlV3 queue_delay_ms timeout broadcastVariables} c4Rest {useCloudAuth useCertificates netSubTimeoutDelay timeout hotDashDriver hotDashOccupancyDriver} fias {enabled host port reconnectDelay reSyncAfterReconnection reSyncAfterReconnectionDelay keepAliveDelay keepAliveMessageDelay handshakeDelay roomMapping {useStatic useRule roomMap {pms hd} rule {type regex regexRepl}} } }}"}`;
        const body = JSON.stringify({
            query: `
    query {
      dcsConfig {
        dcsId
        siteId
        queue {
          q_limit
          p_limit
          s_limit
          time
          time_other
        }
        projectParsing {
          useStaticMapping
          roomNumberSuffix
          leadingZeros
        }
        hotdash_api {
          urlV3
          queue_delay_ms
          timeout
          broadcastVariables
        }
        c4Rest {
          useCloudAuth
          useCertificates
          netSubTimeoutDelay
          timeout
          hotDashDriver
          hotDashOccupancyDriver
        }
        fias {
          enabled
          host
          port
          reconnectDelay
          reSyncAfterReconnection
          reSyncAfterReconnectionDelay
          keepAliveDelay
          keepAliveMessageDelay
          handshakeDelay
          roomMapping {
            useStatic
            useRule
            roomMap {
              pms
              hd
            }
            rule {
              type
              regex
              regexRepl
            }
          }
        }
        ohip {
            enabled
            reconnectDelay
            keepAliveDelay
            keepAliveMessageDelay
            reSyncAfterReconnection
            reSyncAfterReconnectionDelay
            poll
            pollInterval
            reprovisionTime
            ohipUrl
            ohipOCIM
            ohipEnterpriseId
            ohipUser
            ohipClient
            ohipHotelId
            ohipAppKey
            ohipExtSysCode
            ohipChainCode
            ohipQueryLimit
            serviceEnabled
            logOnly
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        mews {
            enabled
            platformUrl
            websocketUrl
            clientName
            timeout
            checkInOutEnabled
            murEnabled
            murStateReason
            dedupTtlSeconds
            murAsTask
            dndAsTask
            task {
                departmentId
                departmentName
                deadlineOffsetMinutes
            }
            reconnectDelaySeconds
            reconnectMaxDelaySeconds
            resourceRefreshMinutes
            msgMap {
                state
                cmd
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                ruleReverse {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        inncom {
            enabled
            host
            port
            site
            reconnectDelay
            handshakeDelay
            keepAliveDelay
            keepAliveMessageDelay
            reSyncAfterReconnection
            reSyncAfterReconnectionDelay
            parseInnProjFile
            skipExistingRooms
            clearStructure
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    hd
                    pms
                }
            }
        }
        axxess {
            enabled
            reSyncAfterReconnection
            parseFloorFromRoom
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        stayntouch {
            enabled
            hotelId
            timeout
            reSyncAfterReconnection
            reSyncAfterReconnectionDelay
            authBaseUrl
            apiBaseUrl
            clientId
            grantType
            msgMap {
                event
                cmd
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        hubos {
            enabled
            baseUrl
            id
            accessToken
            roomMapping {
                useRule
                useStatic
                roomMap {
                    pms
                    hd
                }
                rule {
                    type
                    regex
                    regexRepl
                }
            }
        }
        runway {
            enabled
            logLevel
            authUrl
            operatorUrl
            subscriptionUrl
            timeout
            auth {
                username
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        unifocus {
            enabled
            baseUrl
            propertyId
            publicKey
            complains {
                ClosedReasonCode
                dnd {
                    callDescription
                    category
                }
                hsk {
                    callDescription
                    category
                }
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        alice {
            enabled
            hotelId
            timeout
            apiBaseUrl
            apiKey
            username
            dndEnabled
            murEnabled
            murAsTicket
            serviceRequest {
                facilityId
                serviceId
                info
                dueOffsetMinutes
                apiUserId
                cancelWorkflowStatusId
                options {
                    name
                    value
                }
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        lutron {
            enabled
            reSyncAfterReconnection
            reSyncAfterReconnectionDelay
            buttons {
                    dndButtonNames
                    hskButtonNames
                }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        belden {
            enabled
            baseUrl
            apiPrefix
            rejectUnauthorized
            timeout
            keepAliveMs
            maxSockets
            username
            siteId
            cloudSync
            poll {
                statusMs
                alarmsMs
                inventoryMs
                domMs
                uniMs
            }
            ws {
                enabled
                path
                reconnectMinMs
                reconnectMaxMs
                stableMs
                handshakeMs
                lifecycleDebounceMs
            }
            roomParsing {
                sourceField
                descriptionRegex
                unassignedRoom
            }
        }
        vostio {
            enabled apiUrl eventsUrl acceptHeader username clientId userPoolId authBearer timeout
            tokenRefreshMarginSeconds doorEventsEnabled keyEventsEnabled keyEventMediaTypes syncOnConnect
            pingIntervalSeconds pongTimeoutSeconds maxConnectionMinutes reconnectDelaySeconds reconnectMaxDelaySeconds
            dedupCacheSize publishRawEvents checkoutEventEnabled publishVariables varMap doorMap
            structure { enabled commonRoomPms guestRoomPattern modelIds { system guestRoom commonDoor } discoveryDelayMs syncOnStart resyncMinutes }
            floorParsing { useProjectParsing useStatic staticMap useRule rule roomNumberSuffix }
            roomMapping { useStatic useRule rule { type regex regexRepl } ruleReverse { type regex regexRepl } roomMap { pms hd } }
        }
        bacnet {
            enabled interface port broadcastAddress deviceId deviceName vendorId
            bbmd { address port ttlSeconds }
            roomGrouping roomPrefix nameTemplate nameMaxLength alwaysSuffixDevId
            expose { models rooms excludeRooms }
            autoProvision pulseHoldSeconds staleAfterSeconds persistence
            feed { enabled stream group blockMs batch }
            reconcile exposeSourceLinks exposeGatewayView models devices sources
        }
        fiasServer {
            enabled host port handshakeDelay keepAliveDelay keepAliveMessageDelay sendUnsubscribedRecords
            requireInboundKeepAlive timezone interfaceFamily version
            roomMapping { useStatic useRule rule { type regex regexRepl } roomMap { pms hd } }
        }
      }
    }
  `
        });
        return this._httpClient.post<DcsConfig[]>(getBackendUri() + '/graphql', body, this._httpOptions)
            .pipe(
                tap((dcsConfigs) => {
                    if (dcsConfigs) {
                        dcsConfigs = removeEmpty(dcsConfigs);
                        this._dcsConfigs.next(dcsConfigs['data']['dcsConfig'].map((dcsConfig: DcsConfig) => {
                            return {
                                ...dcsConfig,
                            };
                        }));
                    } else {
                        this._dcsConfigs.next([]);
                    }
                })
            );
    }

    pullDcsConfigFromPremises(dcsId: number): Observable<any> {
        const query = 'query pullDcsConfig($input: DcsIdInput) { pullDcsConfig(input: $input) {statusCode}}';
        const body = JSON.stringify({
            query,
            variables: {input: {dcsId}}
        });
        return this._httpClient.post(getBackendUri() + '/graphql', body, this._httpOptions);
    }

    /**
     * Update dcsConfig
     *
     * @param input
     */
    updateDcsConfig(input: any): Observable<any> {
        const query = `
  mutation updateDcsConfig($input: DcsConfigInput) {
    updateDcsConfig(input: $input) {
      dcsId
        siteId
        queue {
          q_limit
          p_limit
          s_limit
          time
          time_other
        }
        projectParsing {
          useStaticMapping
          roomNumberSuffix
          leadingZeros
        }
        hotdash_api {
          urlV3
          queue_delay_ms
          timeout
          broadcastVariables
        }
        c4Rest {
          useCloudAuth
          useCertificates
          netSubTimeoutDelay
          timeout
          hotDashDriver
          hotDashOccupancyDriver
        }
        fias {
          enabled
          host
          port
          reconnectDelay
          reSyncAfterReconnection
          reSyncAfterReconnectionDelay
          keepAliveDelay
          keepAliveMessageDelay
          handshakeDelay
          roomMapping {
            useStatic
            useRule
            roomMap {
              pms
              hd
            }
            rule {
              type
              regex
              regexRepl
            }
          }
        }
        ohip {
            enabled
            reconnectDelay
            keepAliveDelay
            keepAliveMessageDelay
            reSyncAfterReconnection
            reSyncAfterReconnectionDelay
            poll
            pollInterval
            reprovisionTime
            ohipUrl
            ohipOCIM
            ohipEnterpriseId
            ohipUser
            ohipClient
            ohipHotelId
            ohipAppKey
            ohipExtSysCode
            ohipChainCode
            ohipQueryLimit
            serviceEnabled
            logOnly
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        mews {
            enabled
            platformUrl
            websocketUrl
            clientName
            timeout
            checkInOutEnabled
            murEnabled
            murStateReason
            dedupTtlSeconds
            murAsTask
            dndAsTask
            task {
                departmentId
                departmentName
                deadlineOffsetMinutes
            }
            reconnectDelaySeconds
            reconnectMaxDelaySeconds
            resourceRefreshMinutes
            msgMap {
                state
                cmd
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                ruleReverse {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        inncom {
            enabled
            host
            port
            site
            reconnectDelay
            handshakeDelay
            keepAliveDelay
            keepAliveMessageDelay
            reSyncAfterReconnection
            reSyncAfterReconnectionDelay
            parseInnProjFile
            skipExistingRooms
            clearStructure
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    hd
                    pms
                }
            }
        }
        axxess {
            enabled
            reSyncAfterReconnection
            parseFloorFromRoom
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        stayntouch {
            enabled
            hotelId
            timeout
            reSyncAfterReconnection
            reSyncAfterReconnectionDelay
            authBaseUrl
            apiBaseUrl
            clientId
            grantType
            msgMap {
                event
                cmd
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        hubos {
            enabled
            baseUrl
            id
            accessToken
            roomMapping {
                useRule
                useStatic
                roomMap {
                    pms
                    hd
                }
                rule {
                    type
                    regex
                    regexRepl
                }
            }
        }
        runway {
            enabled
            logLevel
            authUrl
            operatorUrl
            subscriptionUrl
            timeout
            auth {
                username
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        unifocus {
            enabled
            baseUrl
            propertyId
            publicKey
            complains {
                ClosedReasonCode
                dnd {
                    callDescription
                    category
                }
                hsk {
                    callDescription
                    category
                }
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        alice {
            enabled
            hotelId
            timeout
            apiBaseUrl
            apiKey
            username
            dndEnabled
            murEnabled
            murAsTicket
            serviceRequest {
                facilityId
                serviceId
                info
                dueOffsetMinutes
                apiUserId
                cancelWorkflowStatusId
                options {
                    name
                    value
                }
            }
            roomMapping {
                useRule
                useStatic
                rule {
                    type
                    regex
                    regexRepl
                }
                roomMap {
                    pms
                    hd
                }
            }
        }
        belden {
            enabled
            baseUrl
            apiPrefix
            rejectUnauthorized
            timeout
            keepAliveMs
            maxSockets
            username
            siteId
            cloudSync
            poll { statusMs alarmsMs inventoryMs domMs uniMs }
            ws { enabled path reconnectMinMs reconnectMaxMs stableMs handshakeMs lifecycleDebounceMs }
            roomParsing { sourceField descriptionRegex unassignedRoom }
        }
        vostio {
            enabled apiUrl eventsUrl acceptHeader username clientId userPoolId authBearer timeout
            tokenRefreshMarginSeconds doorEventsEnabled keyEventsEnabled keyEventMediaTypes syncOnConnect
            pingIntervalSeconds pongTimeoutSeconds maxConnectionMinutes reconnectDelaySeconds reconnectMaxDelaySeconds
            dedupCacheSize publishRawEvents checkoutEventEnabled publishVariables varMap doorMap
            structure { enabled commonRoomPms guestRoomPattern modelIds { system guestRoom commonDoor } discoveryDelayMs syncOnStart resyncMinutes }
            floorParsing { useProjectParsing useStatic staticMap useRule rule roomNumberSuffix }
            roomMapping { useStatic useRule rule { type regex regexRepl } ruleReverse { type regex regexRepl } roomMap { pms hd } }
        }
        bacnet {
            enabled interface port broadcastAddress deviceId deviceName vendorId
            bbmd { address port ttlSeconds }
            roomGrouping roomPrefix nameTemplate nameMaxLength alwaysSuffixDevId
            expose { models rooms excludeRooms }
            autoProvision pulseHoldSeconds staleAfterSeconds persistence
            feed { enabled stream group blockMs batch }
            reconcile exposeSourceLinks exposeGatewayView models devices sources
        }
        fiasServer {
            enabled host port handshakeDelay keepAliveDelay keepAliveMessageDelay sendUnsubscribedRecords
            requireInboundKeepAlive timezone interfaceFamily version
            roomMapping { useStatic useRule rule { type regex regexRepl } roomMap { pms hd } }
        }
    }
  }
`;
        const body = JSON.stringify({
            query,
            variables: {input}
        });
        return this._httpClient.post(getBackendUri() + '/graphql', body, this._httpOptions).pipe(
            tap((response: any) => {
                // tell other windows (the Interfaces list in the main tab) that this DCS config changed
                if (!response?.errors && typeof BroadcastChannel !== 'undefined') {
                    const channel = new BroadcastChannel(INTERFACES_CHANNEL);
                    channel.postMessage({dcsId: input?.dcsId});
                    channel.close();
                }
            })
        );
    }

    /**
     * Send dcs command
     */
    sendDcsCommand(input: any): Observable<any> {
        const query = 'mutation executeCommandOnDcs($input: DcsCommandInput) { executeCommandOnDcs(input: $input) { statusCode, message, error, data }}';
        const body = JSON.stringify({
            query,
            variables: {input}
        });
        return this._httpClient.post(getBackendUri() + '/graphql', body, this._httpOptions);
    }

    subscribeForLogs(input): Observable<any> {
        const query = 'mutation subscribeToEvents($input: EventSubscriptionInput) { subscribeToEvents(input: $input) { statusCode, message, error, data }}';
        const body = JSON.stringify({
            query,
            variables: {input}
        });
        return this._httpClient.post(getBackendUri() + '/graphql', body, this._httpOptions);
    }

    createNewDcs(input: any): Observable<any> {
        const query = 'mutation createDcs($input: createDcs) { createDcs(input: $input) { id email site {id name} }}';
        const body = JSON.stringify({
            query,
            variables: {input}
        });
        return this._httpClient.post(getBackendUri() + '/graphql', body, this._httpOptions);
    }

    getVariables(): Observable<Variable[]> {
        const query = 'query { variableDefinition { id name } }';
        const body = JSON.stringify({
            query
        });
        return this._httpClient.post<Variable[]>(getBackendUri() + '/graphql', body, this._httpOptions)
            .pipe(
                tap((variables) => {
                    if (variables) {
                        this._variables.next(variables['data']['variableDefinition']);
                    } else {
                        this._variables.next([]);
                    }
                })
            );
    }
}
